import React, {
  useEffect,
  useRef,
  useState,
  FocusEvent,
  MouseEvent
} from 'react'
import classNames from 'classnames'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'
import { DateInputProps } from './DateInput.d'
import { getIsFirefox } from '@toasttab/shared/src/utils/formUtils'

const DateInput2 = ({
  variation,
  id,
  label,
  name,
  min,
  max,
  error,
  value,
  onChange,
  onBlur
}: DateInputProps) => {
  const [hasValue, setHasValue] = useState<boolean>(false)
  const dateInputElement = useRef<HTMLInputElement>(null)
  const isFirefox = getIsFirefox()

  useEffect(() => {
    if (isFirefox) {
      if (dateInputElement.current) {
        if (dateInputElement.current.value === '') {
          dateInputElement.current.type = 'text'
        }
        dateInputElement.current.addEventListener('onFocus', (event: any) => {
          event.target.type = 'date'
        })
        dateInputElement.current.addEventListener('blur', (event: any) => {
          if (event.target.value === '') {
            event.target.type = 'text'
            setHasValue(false)
          }
        })
      }
    }
  }, [])

  const dateWrapper = classNames({
    'w-full': true
  })
  const errorClassNames = classNames({
    'absolute top-full w-max flex flex-row font-sansPro items-center text-error text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'default',
    'absolute top-full w-max flex flex-row font-sansPro items-center text-white text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline-inverse before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'inverse'
  })

  const inputClassNames = classNames(
    'bg-white text-base placeholder:text-gray-50 text-default rounded-[4px] font-sansPro min-w-[95%] min-h-[50px]',
    {
      // Full Width
      'w-full': true,
      // Border shadow
      'border border-gray-50 shadow-none text-base box-border peer/input':
        (variation === 'inverse' || variation === 'default') && !error,
      'border border-otl-error border-error': error,
      'placeholder-transparent':
        (variation === 'inverse' || variation === 'default') && label !== '',

      // Padding
      'py-3 px-[10px]': true,
      // Focus Padding
      'focus-within:pt-[18px] focus-within:pb-[6px]': label,
      'pt-[18px] pb-[6px]': label && hasValue
    }
  )
  const labelClassNames = classNames({
    hidden: !label && variation !== 'default' && variation !== 'inverse',
    'font-sansPro font-normal text-left text-secondary tracking-[0.2px] pointer-events-none h-[24px] mb-[3px]':
      true,
    'text-base leading-[20px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      !hasValue,
    'absolute top-[15px] left-[10px] right-[10px] peer-focus/input:text-[10px] peer-focus/input:leading-[10px] peer-focus/input:top-[10px]':
      label &&
      (variation === 'inverse' || variation === 'default') &&
      !hasValue,
    'absolute left-[10px] right-[10px] text-[10px] leading-[10px] top-[10px]':
      label && (variation === 'inverse' || variation === 'default') && hasValue
  })

  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    try {
      if (error) {
        setHasValue(true)
      }
      e.currentTarget.showPicker()
    } catch (ex) {}
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (e.currentTarget.value) {
      setHasValue(true)
    } else {
      setHasValue(false)
    }

    if (isFirefox) {
      if (dateInputElement && dateInputElement.current) {
        if (dateInputElement.current.value === '')
          dateInputElement.current.type = 'text'
      }
    }
  }

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (isFirefox) {
      if (dateInputElement && dateInputElement.current) {
        dateInputElement.current.type = 'date'
      }
      if (error) {
        setHasValue(true)
      }
      e.currentTarget.showPicker()
    }
  }

  return (
    <div className={`${dateWrapper} date-wrapper`} data-testid='date-picker'>
      <input
        className={inputClassNames}
        data-testid='date-picker-input'
        id={id}
        max={max}
        min={min}
        name={name}
        onBlur={onBlur ? handleBlur : null}
        onChange={onChange}
        onClick={handleClick}
        onFocus={handleFocus}
        placeholder=''
        ref={dateInputElement}
        type='date'
        value={value}
      />

      {label ? (
        <label
          className={labelClassNames}
          data-testid='date-picker-label'
          htmlFor={id}
        >
          {label}
        </label>
      ) : null}
      <div className='calendar-icon-wrapper' data-testid='date-picker-icon'>
        <img alt='' src={resolvePrefix('/svg/icon-calendar.svg')} />
      </div>
      {error ? (
        <div
          className={errorClassNames}
          data-testid='date-picker-error'
          role='alert'
        >
          {error}
        </div>
      ) : null}
    </div>
  )
}

export default DateInput2
